import { computed, onMounted, shallowRef, toValue } from "vue";
import { useEventListener, useResizeObserver } from "@vueuse/core";

const PAGE_HEADER = ".site-header-container, .page-header-wrapper";

function covers(header) {
  const { position } = getComputedStyle(header);
  if (position === "sticky" || position === "fixed") {
    return true;
  }
  if (position !== "absolute") {
    return false;
  }
  const rect = header.getBoundingClientRect();
  return rect.height > 0 && rect.top <= 1 && rect.bottom > 0;
}

/** Returns the page header that covers the top of the viewport: a sticky, fixed or top-anchored known wrapper or `<header>`. */
function coveringHeader(document) {
  return [...document.querySelectorAll(PAGE_HEADER), ...document.querySelectorAll("header")].find(covers);
}

/** Returns the header the mobile overlay starts below: a known wrapper whatever its position, else the covering header. */
function overlayHeader(document) {
  return document.querySelector(PAGE_HEADER) ?? coveringHeader(document);
}

/**
 * Measures page chrome and the owning surface only, while the owner is mounted.
 *   - offset:        where the canvas sticks, below the page header and the results header
 *   - headerOffset:  where the sidebar's date row sticks, below the page header only
 */
export function useFinderMapLayout({ canvas, sidebar, fallback = 0 }) {
  const offset = shallowRef(Number(toValue(fallback)) || 0);
  const headerOffset = shallowRef(0);
  const header = shallowRef();
  function measure() {
    const element = toValue(canvas);
    const document = element?.ownerDocument;
    if (!document) {
      return;
    }
    const results = element.closest("sp-finder")?.querySelector("#results-header");
    const resultsTop = results ? Number.parseFloat(getComputedStyle(results).top) || 0 : 0;
    const pageHeader = toValue(sidebar)
      ? Math.max(0, coveringHeader(document)?.getBoundingClientRect().bottom || 0)
      : overlayHeader(document)?.offsetHeight || 0;
    headerOffset.value = pageHeader;
    offset.value = Math.max(
      Number(toValue(fallback)) || 0,
      pageHeader,
      resultsTop + (results?.getBoundingClientRect().height || 0),
    );
  }
  function findPageHeader() {
    const document = toValue(canvas)?.ownerDocument;
    header.value = document && (toValue(sidebar) ? coveringHeader(document) : overlayHeader(document));
    measure();
  }
  const observed = computed(() => [toValue(canvas), toValue(sidebar)?.$el, header.value].filter(Boolean));
  useResizeObserver(observed, measure);
  useEventListener(window, "resize", measure);
  onMounted(findPageHeader);
  return { offset, headerOffset, measure };
}
